<script setup>
import { provide, useId } from 'vue';
import {
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyTitle,
} from 'dashboard/components-next/polaris/ui/empty';

defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  noRecordsFound: {
    type: Boolean,
    default: false,
  },
  loadingMessage: {
    type: String,
    default: '',
  },
  noRecordsMessage: {
    type: String,
    default: '',
  },
});

// The header (BaseSettingsHeader) sends its introduction line and its search here, to the top of the content, as
// the kit's SettingsArea draws them: the title and actions stay still, the rest scrolls.
const introId = useId();
provide('settingsIntroTarget', `[data-settings-intro="${introId}"]`);
</script>

<!-- The mould of a settings page, on the kit's SettingsArea: header still (shrink-0), content scrolling under it
     (px-6 py-6, a gap-4 grid up to max-w-[100rem]). Loading = the kit spinner; no records = the kit's Empty with the
     page's sentence and, when the page gives one, its create action (slot `emptyActions`).
     Slot `drawer` (phase 9.3): the page's EditSheet, beside header and content — a column from 1280px, the
     non-modal overlay below (DrawerFrame). -->
<template>
  <div
    data-settings-layout
    class="flex h-full min-h-0 w-full flex-1 text-polaris-foreground"
  >
    <div class="flex min-h-0 min-w-0 flex-1 flex-col">
      <slot name="header" />
      <div class="min-h-0 flex-1 overflow-auto px-6 py-6">
        <div class="mx-auto grid w-full max-w-[100rem] gap-4">
          <div :data-settings-intro="introId" class="contents" />
          <!-- Added to render any templates that should be rendered before body -->
          <slot name="preBody" />
          <slot v-if="isLoading" name="loading">
            <woot-loading-state :message="loadingMessage" />
          </slot>
          <Empty
            v-else-if="noRecordsFound"
            class="border border-polaris-border md:p-8"
          >
            <EmptyHeader>
              <EmptyTitle>{{ noRecordsMessage }}</EmptyTitle>
            </EmptyHeader>
            <EmptyContent v-if="$slots.emptyActions">
              <slot name="emptyActions" />
            </EmptyContent>
          </Empty>
          <slot v-else name="body" />
          <!-- Do not delete the slot below. It is required to render anything that is not defined in the above slots. -->
          <slot />
        </div>
      </div>
    </div>
    <slot name="drawer" />
  </div>
</template>
